import React from "react";
import { Branding } from "bcms-react";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { FormContainer, FormRowView, WidgetContainer } from "components/shared";
import { UICheckbox, UITextInput } from "components/ui";
import StatesCustomSelect from "components/custom-ui/StatesCustomSelect";
import CountriesCustomSelect from "components/custom-ui/CountriesCustomSelect";

import "./styles.css";

/**
 * @function WirePayeeInformationWidget
 * @param {STRING} payeeName - Sets the wire payee name text.
 * @param {STRING} payeeEmailAddress - Sets the wire payee email address text.
 * @param {STRING} payeeAccountNumber - Sets the wire payee account number text.
 * @param {STRING} payeeConfirmAccountNumber - Sets the wire payee confirm account number text.
 * @param {STRING} payeeAddressLineOne - Sets the wire payee address line 1 text.
 * @param {STRING} payeeAddressLineTwo - Sets the wire payee address line 2 text.
 * @param {STRING} payeeCity - Sets the wire payee city text.
 * @param {STRING} payeeState - Sets the wire payee state text.
 * @param {STRING} payeeZip - Sets the wire payee zip text.
 * @param {STRING} payeePostalCode - Sets the wire payee postal code text.
 * @return {React.Component}
 * @exported true
 * @description This is Wire Payee Information widget for OneTimeWirePage
 */
export default function WirePayeeInformationWidget(props) {
    const data = props.data ?? {};
    const ui = props.ui ?? {};
    const action = props.action ?? {};
    const oneTimeWireTransfer = data.oneTimeWireTransfer ?? {};
    const sendEmailWhenWireStatusDownloaded = oneTimeWireTransfer.sendEmailWhenWireStatusDownloaded ?? false;
    const payeeType = oneTimeWireTransfer.payeeType ? oneTimeWireTransfer.payeeType.toLowerCase() : undefined;
    const payeeName = props.payeeName ?? "[payeeName]";
    const payeeEmailAddress = props.payeeEmailAddress ?? "[payeeEmailAddress]";
    const payeeAccountNumber = props.payeeAccountNumber ?? "[payeeAccountNumber]";
    const payeeConfirmAccountNumber = props.payeeConfirmAccountNumber ?? "[payeeConfirmAccountNumber]";
    const payeeAddressLineOne = props.payeeAddressLineOne ?? "[payeeAddressLineOne]";
    const payeeAddressLineTwo = props.payeeAddressLineTwo ?? "[payeeAddressLineTwo]";
    const payeeCity = props.payeeCity ?? "[payeeCity]";
    const payeeState = props.payeeState ?? "[payeeState]";
    const payeeZip = props.payeeZip ?? "[payeeZip]";
    const payeePostalCode = props.payeePostalCode ?? "[payeePostalCode]";

    return (
        <WidgetContainer className="wire-payee-information-widget">
            <h2 className="widget-header">Payee Information</h2>
            <FormContainer className="small-form-width">
                <FormRowView className="payee-type" text="* Payee Type">
                    <Branding id="international_wires_abi22" value="false">
                        <CustomSelect active={ ui.payeeTypeSelectIsActive } value={ payeeType } onChange={ action.onChangeOneTimeWireProp("payeeType") }>
                            <CustomSelectItem value="domestic">Domestic</CustomSelectItem>
                        </CustomSelect>
                    </Branding>
                    <Branding id="international_wires_abi22" value="true">
                        <CustomSelect active={ ui.payeeTypeSelectIsActive } value={ payeeType } onChange={ action.onChangeOneTimeWireProp("payeeType") }>
                            <CustomSelectItem value="domestic">Domestic</CustomSelectItem>
                            <CustomSelectItem value="international">International</CustomSelectItem>
                        </CustomSelect>
                    </Branding>
                </FormRowView>
                <FormRowView className="payee-name" text="* Payee Name">
                    <UITextInput value={ oneTimeWireTransfer.name } onChange={ action.onChangeOneTimeWireProp("name") } />
                </FormRowView>

                {/* Email address textfield is shown if the "Send email when..." checkbox is checked. */}
                <FormRowView className="send-email" text="">
                    <UICheckbox value={ sendEmailWhenWireStatusDownloaded } onChange={ action.onChangeOneTimeWireProp("sendEmailWhenWireStatusDownloaded") }
                    label="Send email when wire status is changed to Downloaded" />
                </FormRowView>
                { sendEmailWhenWireStatusDownloaded &&
                <FormRowView className="email-address" text="* Email Address">
                    <UITextInput value={ oneTimeWireTransfer.payeeEmailAddress } onChange={ action.onChangeOneTimeWireProp("payeeEmailAddress") } />
                </FormRowView>
                }

                <FormRowView className="account-number" text="* Account Number">
                    <UITextInput value={ oneTimeWireTransfer.payeeAccountNumber } onChange={ action.onChangeOneTimeWireProp("payeeAccountNumber") } />
                </FormRowView>
                <FormRowView className="confrim-account-number" text="* Confirm Account Number">
                    <UITextInput value={ oneTimeWireTransfer.payeeConfirmAccountNumber } onChange={ action.onChangeOneTimeWireProp("payeeConfirmAccountNumber") } />
                </FormRowView>
                <FormRowView className="address-line-one" text="* Address Line 1">
                    <UITextInput value={ oneTimeWireTransfer.addressLine1 } onChange={ action.onChangeOneTimeWireProp("addressLine1") } />
                </FormRowView>
                <FormRowView className="address-line-two" text="Address Line 2">
                    <UITextInput value={ oneTimeWireTransfer.addressLine2 } onChange={ action.onChangeOneTimeWireProp("addressLine2") } />
                </FormRowView>
                <FormRowView className="city" text="* City">
                    <UITextInput value={ oneTimeWireTransfer.city } onChange={ action.onChangeOneTimeWireProp("city") } />
                </FormRowView>

                {/* Domestic form rows is shown if "Domestic" Payee Type is selected */}
                { payeeType === "domestic" && 
                <div className="form-rows domestic-form-rows">
                    <FormRowView className="state" text="* State">
                        <StatesCustomSelect active={ ui.stateSelectIsActive } value={ oneTimeWireTransfer.state } onChange={ action.onChangeOneTimeWireProp("state") } />
                    </FormRowView>
                    <FormRowView className="zip-code" text="* ZIP Code">
                        <UITextInput value={ oneTimeWireTransfer.zip } onChange={ action.onChangeOneTimeWireProp("zip") } />
                    </FormRowView>
                </div>
                }

                {/* International form rows is shown if "International" Payee Type is selected */}
                { payeeType === "international" && 
                <div className="form-rows international-form-rows">
                    <FormRowView className="state-or-province" text="State or Province">
                        <UITextInput value={ oneTimeWireTransfer.stateOrProvidence } onChange={ action.onChangeOneTimeWireProp("stateOrProvidence") } />
                    </FormRowView>
                    <FormRowView className="postal-code" text="Postal Code">
                        <UITextInput value={ oneTimeWireTransfer.postalCode } onChange={ action.onChangeOneTimeWireProp("postalCode") } />
                    </FormRowView>
                    <FormRowView className="country" text="* Country">
                        <CountriesCustomSelect active={ ui.countrySelectIsActive } value={ oneTimeWireTransfer.country } onChange={ action.onChangeOneTimeWireProp("country") } />
                    </FormRowView>
                </div>
                }

            </FormContainer>
        </WidgetContainer>
    )
}

